import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Save, Camera, Trash2, Plus, X, Building2, MapPin, Wrench,
  Search, Upload, Image as ImageIcon, FileText, Clock, User, PenLine, FileDown, Send, CheckCircle2,
} from "lucide-react";
import { api, errMsg } from "@/lib/config";
import SignaturePad from "@/components/SignaturePad";

const VISIT_TYPES = [
  { key: "preventive", label: "Manutenzione preventiva", color: "text-[#22c55e]" },
  { key: "corrective", label: "Intervento correttivo", color: "text-[#ff4b00]" },
  { key: "emergency", label: "Emergenza", color: "text-red-400" },
];

const UNITS = ["pz", "mt", "kg", "lt", "cad", "ml", "h"];

export default function AdminReportEditor({ report, onClose }) {
  const isNew = !report?.id;
  const [saving, setSaving] = useState(false);
  const [currentReport, setCurrentReport] = useState(report || null);
  const [f, setF] = useState({
    customer_id: report?.customer_id || "",
    plant_id: report?.plant_id || "",
    visit_type: report?.visit_type || "preventive",
    intervention_date: report?.intervention_date || new Date().toISOString().slice(0, 10),
    start_time: report?.start_time || "",
    end_time: report?.end_time || "",
    technician_name: report?.technician_name || "",
    work_description: report?.work_description || "",
    materials_used: report?.materials_used || [],
    photos: report?.photos || [],
    is_covered_by_contract: report?.is_covered_by_contract !== false,
    notes_admin: report?.notes_admin || "",
  });

  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [showCustList, setShowCustList] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [plant, setPlant] = useState(null);

  // Load customers on mount
  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  // When customer changes, load its plants
  useEffect(() => {
    if (!f.customer_id) { setPlants([]); setPlant(null); return; }
    const c = customers.find((x) => x.id === f.customer_id);
    if (c) setCustomer(c);
    api.get("/admin/plants", { params: { customer_id: f.customer_id } })
      .then((r) => setPlants(r.data || []))
      .catch(() => {});
  }, [f.customer_id, customers]);

  useEffect(() => {
    if (!f.plant_id) { setPlant(null); return; }
    const p = plants.find((x) => x.id === f.plant_id);
    if (p) setPlant(p);
  }, [f.plant_id, plants]);

  const filteredCustomers = customers.filter((c) =>
    !custQuery ||
    (c.name || "").toLowerCase().includes(custQuery.toLowerCase()) ||
    (c.ragione_sociale || "").toLowerCase().includes(custQuery.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(custQuery.toLowerCase())
  );

  const selectCustomer = (c) => {
    setF({ ...f, customer_id: c.id, plant_id: "" });
    setCustomer(c);
    setShowCustList(false);
    setCustQuery("");
  };

  const save = async () => {
    if (!f.customer_id) { toast.error("Seleziona un cliente"); return; }
    if (!f.technician_name.trim()) { toast.error("Nome tecnico obbligatorio"); return; }
    if (!f.work_description.trim()) { toast.error("Descrizione lavori obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        materials_used: f.materials_used.filter((m) => m.name && m.qty > 0),
        photos: f.photos,
      };
      let saved;
      if (!currentReport?.id) {
        const r = await api.post("/admin/reports/v2", payload);
        saved = r.data;
        toast.success(`Rapporto ${saved.report_number} creato`);
      } else {
        const r = await api.put(`/admin/reports/v2/${currentReport.id}`, payload);
        saved = r.data;
        toast.success("Rapporto aggiornato");
      }
      setCurrentReport(saved);
    } catch (e) {
      toast.error(errMsg(e, "Errore nel salvataggio"));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onClose} data-testid="report-back" className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00] flex items-center gap-2 mb-6">
        <ArrowLeft size={12} /> Torna ai rapporti
      </button>

      <div className="flex items-start justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> {currentReport?.id ? `Rapporto ${currentReport.report_number || ""}` : "Nuovo rapporto"}</div>
          <div className="font-display text-3xl md:text-4xl">Intervento tecnico</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="report-save" className="btn-primary shrink-0">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Salvataggio..." : (currentReport?.id ? "Aggiorna rapporto" : "Salva rapporto")}
        </button>
      </div>

      {/* CLIENTE + IMPIANTO */}
      <Section icon={Building2} title="Cliente & Impianto">
        <div className="relative">
          <label className="field-label">Cliente *</label>
          {customer && !showCustList ? (
            <div className="mt-2 border border-[#1a1a1a] bg-[#0a0a0a] p-4 flex items-center justify-between">
              <div>
                <div className="text-white font-semibold">{customer.name}</div>
                <div className="font-mono-tech text-[10px] text-white mt-1">{customer.ragione_sociale || customer.email}</div>
              </div>
              <button type="button" onClick={() => { setCustomer(null); setF({ ...f, customer_id: "", plant_id: "" }); setShowCustList(true); }} className="text-white hover:text-[#ff4b00] p-2"><X size={14} /></button>
            </div>
          ) : (
            <>
              <div className="relative mt-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white" />
                <input
                  value={custQuery}
                  onChange={(e) => { setCustQuery(e.target.value); setShowCustList(true); }}
                  onFocus={() => setShowCustList(true)}
                  placeholder="Cerca cliente per nome, ragione sociale, email..."
                  className="field pl-10 w-full"
                  data-testid="report-customer-search"
                />
              </div>
              {showCustList && (
                <div className="mt-2 border border-[#1a1a1a] bg-[#080808] max-h-64 overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <div className="p-4 text-center font-mono-tech text-[11px] text-white">Nessun cliente trovato</div>
                  ) : (
                    filteredCustomers.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectCustomer(c)}
                        data-testid={`report-cust-opt-${c.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-[#141414] border-b border-[#141414] last:border-0"
                      >
                        <div className="text-white text-sm">{c.name}</div>
                        <div className="font-mono-tech text-[10px] text-white mt-0.5">{c.ragione_sociale || c.email}{c.city && ` · ${c.city}`}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {customer && (
          <div className="mt-4">
            <label className="field-label">Impianto (opzionale)</label>
            {plants.length === 0 ? (
              <div className="mt-2 border border-dashed border-[#1a1a1a] p-3 font-mono-tech text-[10px] text-white">Nessun impianto registrato per questo cliente</div>
            ) : (
              <select
                value={f.plant_id}
                onChange={(e) => setF({ ...f, plant_id: e.target.value })}
                className="field mt-2 w-full"
                data-testid="report-plant-select"
              >
                <option value="">— Nessuno / generico —</option>
                {plants.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.address ? ` · ${p.address}` : ""}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </Section>

      {/* INTERVENTO */}
      <Section icon={Clock} title="Dettagli intervento">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="field-label">Tipo intervento</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {VISIT_TYPES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setF({ ...f, visit_type: v.key })}
                  data-testid={`report-visit-${v.key}`}
                  className={`p-3 border font-mono-tech text-[11px] uppercase ${f.visit_type === v.key ? "border-[#ff4b00] bg-[#ff4b00]/5 text-[#ff4b00]" : "border-[#1a1a1a] text-white hover:text-white"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="field-label">Data intervento *</label>
            <input required type="date" value={f.intervention_date} onChange={(e) => setF({ ...f, intervention_date: e.target.value })} className="field mt-2 w-full" data-testid="report-date" />
          </div>
          <div>
            <label className="field-label">Nome tecnico *</label>
            <input required value={f.technician_name} onChange={(e) => setF({ ...f, technician_name: e.target.value })} className="field mt-2 w-full" placeholder="Es. Luca Bianchi" data-testid="report-technician" />
          </div>
          <div>
            <label className="field-label">Ora inizio</label>
            <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="field mt-2 w-full" />
          </div>
          <div>
            <label className="field-label">Ora fine</label>
            <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="field mt-2 w-full" />
          </div>
        </div>
        <label className="flex items-center gap-3 mt-4 cursor-pointer">
          <input type="checkbox" checked={f.is_covered_by_contract} onChange={(e) => setF({ ...f, is_covered_by_contract: e.target.checked })} className="accent-[#ff4b00]" />
          <span className="text-sm text-white">Intervento coperto da contratto di manutenzione</span>
        </label>
      </Section>

      {/* LAVORI SVOLTI */}
      <Section icon={Wrench} title="Lavori svolti">
        <label className="field-label">Descrizione dettagliata *</label>
        <textarea
          required
          value={f.work_description}
          onChange={(e) => setF({ ...f, work_description: e.target.value })}
          rows={6}
          className="field mt-2 w-full"
          placeholder="Descrivi cosa è stato fatto durante l'intervento..."
          data-testid="report-description"
        />
      </Section>

      {/* MATERIALI */}
      <Section icon={FileText} title="Materiali utilizzati">
        <MaterialsList
          items={f.materials_used}
          onChange={(list) => setF({ ...f, materials_used: list })}
        />
      </Section>

      {/* FOTO */}
      <Section icon={Camera} title="Foto intervento">
        <PhotoUploader
          photos={f.photos}
          onChange={(list) => setF({ ...f, photos: list })}
        />
      </Section>

      {/* NOTE ADMIN */}
      <Section icon={User} title="Note interne (non nel PDF)">
        <textarea
          value={f.notes_admin}
          onChange={(e) => setF({ ...f, notes_admin: e.target.value })}
          rows={2}
          className="field w-full"
          placeholder="Annotazioni visibili solo all'admin"
        />
      </Section>

      {/* FIRMA + PDF + INVIO — richiede prima il salvataggio */}
      {currentReport?.id ? (
        <SignAndSendSection
          report={currentReport}
          onReportUpdated={setCurrentReport}
        />
      ) : (
        <div className="mb-8 border border-dashed border-[#333] bg-[#0a0a0a] p-5 text-center">
          <div className="font-mono-tech text-[10px] uppercase text-white mb-1">◐ Firma & PDF</div>
          <div className="text-white text-sm">Salva prima il rapporto per attivare firma cliente, PDF e invio email.</div>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center pb-8">
        <button onClick={save} disabled={saving} data-testid="report-save-bottom" className="btn-primary w-full sm:w-auto justify-center">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Salvataggio..." : (currentReport?.id ? "Aggiorna rapporto" : "Salva rapporto")}
        </button>
        <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center" data-testid="report-close">
          Chiudi
        </button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#1a1a1a]">
        <div className="w-8 h-8 border border-[#ff4b00]/30 bg-[#ff4b00]/5 flex items-center justify-center text-[#ff4b00]">
          <Icon size={14} />
        </div>
        <div className="font-mono-tech text-[11px] uppercase text-white">{title}</div>
      </div>
      {children}
    </div>
  );
}

// ─── Materials list with autocomplete ─────────────
function MaterialsList({ items, onChange }) {
  const add = () => onChange([...items, { name: "", unit: "pz", qty: 1, notes: "" }]);
  const update = (idx, upd) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], ...upd };
    onChange(arr);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div>
      {items.length === 0 && (
        <div className="border border-dashed border-[#222] p-4 text-center font-mono-tech text-[11px] text-white mb-3">
          Nessun materiale aggiunto
        </div>
      )}
      <div className="space-y-3">
        {items.map((m, idx) => (
          <MaterialRow key={idx} m={m} idx={idx} onUpdate={update} onRemove={remove} />
        ))}
      </div>
      <button type="button" onClick={add} data-testid="material-add" className="btn-ghost mt-3">
        <Plus size={12} /> Aggiungi materiale
      </button>
      <div className="mt-2 font-mono-tech text-[10px] text-white">
        ◐ Ogni materiale nuovo viene salvato automaticamente nel catalogo per l&apos;autocompletamento futuro.
      </div>
    </div>
  );
}

function MaterialRow({ m, idx, onUpdate, onRemove }) {
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!focused || !m.name || m.name.length < 2) { setSuggestions([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      api.get("/admin/materials", { params: { q: m.name } })
        .then((r) => setSuggestions(r.data.filter((x) => x.name.toLowerCase() !== m.name.toLowerCase())))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timerRef.current);
  }, [m.name, focused]);

  const pick = (s) => {
    onUpdate(idx, { name: s.name, unit: s.unit || "pz" });
    setSuggestions([]);
    setFocused(false);
  };

  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3" data-testid={`material-row-${idx}`}>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-6 relative">
          <input
            value={m.name}
            onChange={(e) => onUpdate(idx, { name: e.target.value })}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder="Nome materiale (es. Guarnizione OR 4x1)"
            className="field w-full"
            data-testid={`material-name-${idx}`}
            autoComplete="off"
          />
          {focused && suggestions.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 border border-[#1a1a1a] bg-[#080808] max-h-48 overflow-y-auto shadow-lg">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={() => pick(s)}
                  className="w-full text-left px-3 py-2 hover:bg-[#141414] text-sm text-white border-b border-[#141414] last:border-0 flex justify-between"
                >
                  <span>{s.name}</span>
                  <span className="font-mono-tech text-[10px] text-white">{s.unit} · usato {s.usage_count}x</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          type="number"
          value={m.qty}
          onChange={(e) => onUpdate(idx, { qty: parseFloat(e.target.value) || 0 })}
          className="field col-span-6 sm:col-span-2 font-mono-tech text-center"
          step="0.01"
          min="0"
          placeholder="Qtà"
          data-testid={`material-qty-${idx}`}
        />
        <select
          value={m.unit}
          onChange={(e) => onUpdate(idx, { unit: e.target.value })}
          className="field col-span-4 sm:col-span-2 font-mono-tech"
        >
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <button
          type="button"
          onClick={() => onRemove(idx)}
          className="col-span-2 sm:col-span-2 p-2 text-white hover:text-red-400 border border-[#1a1a1a] flex items-center justify-center"
          data-testid={`material-remove-${idx}`}
        >
          <Trash2 size={12} />
        </button>
      </div>
      <input
        value={m.notes || ""}
        onChange={(e) => onUpdate(idx, { notes: e.target.value })}
        placeholder="Note (opzionale)"
        className="field w-full mt-2 text-[12px]"
      />
    </div>
  );
}

// ─── Photo uploader ─────────────────────────────
function PhotoUploader({ photos, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const compressImage = (file, maxDim = 1600, quality = 0.8) => new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (ev) => { img.src = ev.target.result; };
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > h && w > maxDim) { h = (h * maxDim) / w; w = maxDim; }
      else if (h > maxDim) { w = (w * maxDim) / h; h = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" })), "image/jpeg", quality);
    };
    reader.readAsDataURL(file);
  });

  const handleFiles = async (files) => {
    setUploading(true);
    const newPhotos = [...photos];
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        const form = new FormData();
        form.append("file", compressed);
        const r = await api.post("/admin/reports/upload-photo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPhotos.push({ url: r.data.url, caption: "" });
      } catch (e) {
        toast.error(`Errore upload: ${file.name}`);
      }
    }
    onChange(newPhotos);
    setUploading(false);
  };

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p, idx) => (
          <div key={idx} className="relative border border-[#1a1a1a] group aspect-square overflow-hidden" data-testid={`photo-${idx}`}>
            <img src={p.url.startsWith("http") ? p.url : `${backendBase}${p.url}`} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(photos.filter((_, i) => i !== idx))}
              className="absolute top-1 right-1 w-7 h-7 bg-black/70 text-white flex items-center justify-center hover:bg-red-500"
              data-testid={`photo-remove-${idx}`}
            >
              <Trash2 size={12} />
            </button>
            <input
              value={p.caption || ""}
              onChange={(e) => {
                const arr = [...photos]; arr[idx].caption = e.target.value; onChange(arr);
              }}
              placeholder="Didascalia..."
              className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-[11px] px-2 py-1 outline-none border-t border-[#333]"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="photo-add"
          className="aspect-square border border-dashed border-[#333] flex flex-col items-center justify-center gap-2 hover:border-[#ff4b00] hover:bg-[#ff4b00]/5 disabled:opacity-40"
        >
          {uploading ? (
            <><Loader2 size={20} className="animate-spin text-[#ff4b00]" /><span className="font-mono-tech text-[10px] text-white">Upload...</span></>
          ) : (
            <><Camera size={24} className="text-white" /><span className="font-mono-tech text-[10px] text-white">Aggiungi foto</span></>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={(e) => { handleFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
        className="hidden"
      />
      <div className="mt-3 font-mono-tech text-[10px] text-white">
        ◐ Su smartphone si apre la fotocamera direttamente. Le foto vengono compresse automaticamente prima dell&apos;upload per risparmiare dati mobili.
      </div>
    </div>
  );
}

// ─── Sign, generate PDF, send email (FASE 3) ─────────────
function SignAndSendSection({ report, onReportUpdated }) {
  const [busy, setBusy] = useState({ pdf: false, send: false });
  const hasTechSig = Boolean(report.technician_signature);
  const hasCustSig = Boolean(report.customer_signature);
  const bothSigned = hasTechSig && hasCustSig;

  const openPdf = async () => {
    setBusy((b) => ({ ...b, pdf: true }));
    try {
      const r = await api.get(`/admin/reports/v2/${report.id}/pdf`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(r.data);
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(errMsg(e, "Errore apertura PDF"));
    } finally {
      setBusy((b) => ({ ...b, pdf: false }));
    }
  };

  const sendEmail = async () => {
    if (!bothSigned && !window.confirm("Il rapporto non è completamente firmato (tecnico + cliente). Inviare comunque?")) return;
    setBusy((b) => ({ ...b, send: true }));
    try {
      const r = await api.post(`/admin/reports/v2/${report.id}/send-pdf`);
      const res = r.data.email_result || {};
      if (res.sent) {
        toast.success(`Email inviata a ${r.data.to}${r.data.cc?.length ? ` (CC ${r.data.cc.join(", ")})` : ""}`);
      } else if (res.queued) {
        toast.info("SMTP non abilitato — email accodata (verrà inviata alla configurazione)");
      } else {
        toast.error("Invio fallito: " + (res.error || "errore SMTP"));
      }
      onReportUpdated({ ...report, sent_to_customer: true, email_sent_at: new Date().toISOString() });
    } catch (e) {
      toast.error(errMsg(e, "Errore invio email"));
    } finally {
      setBusy((b) => ({ ...b, send: false }));
    }
  };

  return (
    <div className="mb-8" data-testid="sign-and-send-section">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#1a1a1a]">
        <div className="w-8 h-8 border border-[#ff4b00]/30 bg-[#ff4b00]/5 flex items-center justify-center text-[#ff4b00]">
          <PenLine size={14} />
        </div>
        <div className="font-mono-tech text-[11px] uppercase text-white">Firme & Invio</div>
        {bothSigned && (
          <div className="ml-auto flex items-center gap-1.5 font-mono-tech text-[10px] uppercase text-[#22c55e]">
            <CheckCircle2 size={12} /> Firmato · completo
          </div>
        )}
        {!bothSigned && (hasTechSig || hasCustSig) && (
          <div className="ml-auto flex items-center gap-1.5 font-mono-tech text-[10px] uppercase text-[#eab308]">
            <CheckCircle2 size={12} /> Firma parziale
          </div>
        )}
      </div>

      {/* Firma tecnico (primo passo — chiude in cantiere) */}
      <div className="mb-5">
        <SignatureBlock
          role="technician"
          report={report}
          onReportUpdated={onReportUpdated}
          label="Firma del tecnico Gefion"
          hint="Il tecnico firma per confermare l'esecuzione dei lavori descritti."
          defaultSignedName={report.technician_signed_name || report.technician_name || ""}
          testId="tech"
        />
      </div>

      {/* Firma cliente (secondo passo — accettazione) */}
      <div>
        <SignatureBlock
          role="customer"
          report={report}
          onReportUpdated={onReportUpdated}
          label="Firma del cliente per accettazione"
          hint="Il cliente firma per accettare i lavori descritti e i materiali utilizzati."
          defaultSignedName={report.customer_signed_name || ""}
          testId="customer"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={openPdf}
          disabled={busy.pdf}
          data-testid="report-pdf-open"
          className="btn-ghost justify-center py-3"
        >
          {busy.pdf ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
          Genera / Apri PDF
        </button>
        <button
          type="button"
          onClick={sendEmail}
          disabled={busy.send}
          data-testid="report-send-email"
          className="btn-primary justify-center py-3"
        >
          {busy.send ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Invia PDF via email
        </button>
      </div>
      {report.email_sent_at && (
        <div className="mt-3 font-mono-tech text-[10px] text-[#22c55e] flex items-center gap-2">
          <CheckCircle2 size={11} /> Email inviata il {new Date(report.email_sent_at).toLocaleString("it-IT")} — copia a info@gefion-evolution.it
        </div>
      )}
    </div>
  );
}


// Reusable per-role signature block (technician or customer).
function SignatureBlock({ role, report, onReportUpdated, label, hint, defaultSignedName, testId }) {
  const padRef = useRef(null);
  const [signedName, setSignedName] = useState(defaultSignedName || "");
  const [padEmpty, setPadEmpty] = useState(true);
  const [busy, setBusy] = useState({ sign: false, clear: false });

  const sigField = `${role}_signature`;
  const nameField = `${role}_signed_name`;
  const dateField = `${role}_signed_at`;
  const hasSig = Boolean(report[sigField]);

  const save = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error("La firma è vuota");
      return;
    }
    if (!signedName.trim()) {
      toast.error("Inserisci il nome del firmatario");
      return;
    }
    setBusy((b) => ({ ...b, sign: true }));
    try {
      const dataUrl = padRef.current.getDataUrl();
      const r = await api.post(`/admin/reports/v2/${report.id}/sign`, {
        signature_data_url: dataUrl,
        signed_name: signedName.trim(),
        role,
      });
      toast.success(role === "technician" ? "Firma tecnico salvata" : "Firma cliente salvata");
      onReportUpdated({
        ...report,
        [sigField]: dataUrl,
        [nameField]: signedName.trim(),
        [dateField]: new Date().toISOString(),
        status: r.data?.status || report.status,
      });
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio firma"));
    } finally {
      setBusy((b) => ({ ...b, sign: false }));
    }
  };

  const clear = async () => {
    const label = role === "technician" ? "del tecnico" : "del cliente";
    if (!window.confirm(`Rimuovere la firma ${label}?`)) return;
    setBusy((b) => ({ ...b, clear: true }));
    try {
      const r = await api.delete(`/admin/reports/v2/${report.id}/sign?role=${role}`);
      toast.success("Firma rimossa");
      onReportUpdated({
        ...report,
        [sigField]: null, [nameField]: null, [dateField]: null,
        status: r.data?.status || "draft",
      });
      padRef.current && padRef.current.clear();
      setSignedName(defaultSignedName || "");
    } catch (e) {
      toast.error(errMsg(e, "Errore rimozione firma"));
    } finally {
      setBusy((b) => ({ ...b, clear: false }));
    }
  };

  const borderTone = role === "technician" ? "border-[#ff4b00]/25" : "border-[#1a1a1a]";
  const bgTone = role === "technician" ? "bg-[#ff4b00]/[0.03]" : "bg-[#0a0a0a]";

  return (
    <div className={`border ${borderTone} ${bgTone} p-4 sm:p-5`} data-testid={`sig-block-${testId}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`font-mono-tech text-[10px] uppercase tracking-wider ${role === "technician" ? "text-[#ff4b00]" : "text-white"}`}>
          ◐ {label}
        </span>
        {hasSig && (
          <span className="ml-auto flex items-center gap-1 font-mono-tech text-[10px] uppercase text-[#22c55e]">
            <CheckCircle2 size={11} /> Firmato
          </span>
        )}
      </div>
      <div className="text-white font-mono-tech text-[10px] mb-3">{hint}</div>

      {hasSig ? (
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={`sig-preview-${testId}`}>
          <div>
            <div className="bg-white p-3 border border-[#333] inline-block">
              <img src={report[sigField]} alt="firma" className="max-h-[100px] max-w-[280px] block" />
            </div>
            <div className="mt-2 text-sm text-white">
              <span className="font-mono-tech text-[10px] uppercase text-white">Firmatario · </span>
              {report[nameField]}
            </div>
            {report[dateField] && (
              <div className="font-mono-tech text-[10px] text-white mt-1">
                {new Date(report[dateField]).toLocaleString("it-IT")}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clear}
            disabled={busy.clear}
            data-testid={`sig-clear-${testId}`}
            className="btn-ghost text-[11px]"
          >
            {busy.clear ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Rimuovi
          </button>
        </div>
      ) : (
        <>
          <SignaturePad
            ref={padRef}
            height={200}
            onChange={(empty) => setPadEmpty(empty)}
            data-testid={`sig-pad-${testId}`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mt-4">
            <div>
              <label className="field-label">Nome firmatario *</label>
              <input
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder={role === "technician" ? "Es. Luca Bianchi (Tecnico Gefion)" : "Es. Mario Rossi (Responsabile Manutenzioni)"}
                className="field mt-2 w-full"
                data-testid={`sig-name-${testId}`}
              />
            </div>
            <div className="sm:self-end">
              <button
                type="button"
                onClick={save}
                disabled={busy.sign || padEmpty || !signedName.trim()}
                data-testid={`sig-save-${testId}`}
                className="btn-primary w-full sm:w-auto justify-center"
              >
                {busy.sign ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salva firma
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

